import { TAwaitingApproval } from '@src/types/status.ts';
import { LeelooAccounts, LeelooUser } from '@src/types/users.ts';
import { TAddons } from '@src/types/packages.ts';
import * as yup from 'yup';

export type TPayout = {
  leeloo_user_id: number;
  leeloo_account_id: number;
  completed_on: string | null;
  approved_on: string | null;
  denied_on: string | null;
  paid_on: string | null;
  rithmic_deduction_added_on: string | null;
  rithmic_deduction_credited_on: string | null;
  rithmic_deduction_by_user_id: number | null;
  rithmic_deduction_credited_by_user_id: number | null;
  created_by_user_id: number | null;
  approved_by_user_id: number | null;
  approved_by_user?: {
    first_name: string | null;
    last_name: string | null;
    id: number | null;
  };
  paid_by_user_id: number | null;
  ach: boolean;
  denied_reason: string | null;
  payout_method: string;
  payment_method_note: string;
  complete_on_ip_address: string | null;
  approved_on_ip_address: string | null;
  is_us_citizen: boolean;
  bank_name: string | null;
  bank_address: string | null;
  bank_city: string | null;
  bank_country: string | null;
  bank_state: string | null;
  bank_zip: string | null;
  bank_swift: string | null;
  bank_iban: string | null;
  bank_code: string | null;
  bank_routing: string | null;
  bank_account: string | null;
  amount: string;
  rithmic_deduction: string;
  note: string | null;
  paypal_address: string | null;
  is_pa: boolean;
  version: number;
  allow_paypal: boolean;
  allow_wire: boolean;
  allow_coupon: boolean;
  allow_riseworks: boolean;
  requires_approval: boolean;
  added_by_user: null;
  riseworks_status: string | null;
  riseworks_payment_intent_object: string | null;
  riseworks_payment_intent_root: string | null;
  riseworks_payment_data_to_sign: string | null;
  riseworks_execute_payment_object: string | null;
  riseworks_execute_payment_response: string | null;
  riseworks_log: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  id: number;
  uuid: string;
  payout_acc_total: number;
  payout_paid_total: string;
  payout_ratio: string;
  leeloo_user: LeelooUser;
  leeloo_account: LeelooAccounts;
  addons: TAddons[];
};

export type PayoutRes = {
  approved_total: string;
  paid_total: string;
  pending_total: string;
  total: string;
  payouts: TPayout[];
};

export type TPaAccountHistoryPayout = {
  id: number;
  created_at: string | null;
  amount: string;
  payout_method: string;
  leeloo_account_id: number;
  completed_on: string | null;
  denied_on: string | null;
  denied_reason: string | null;
  approved_on: string | null;
  paid_on: string | null;
  deleted_at: string | null;
};

export type TPaAccountHistory = {
  leeloo_account_id: number;
  leeloo_account: {
    id: number;
    rithmic_account_name: string;
  };
  payouts: TPaAccountHistoryPayout[];
};

export type PayoutRequest = {
  leeloo_user_id: number;
  leeloo_account_id: number;
  payout_amount: number;
  payout_method: string;
};

export type ActivePayoutRequest = {
  paymentNumber?: number;
  userId?: string;
  userName?: string;
  accountId?: string;
  accountCreatedAt?: string;
  contracts?: number;
  launchAmount?: string;
  addons?: string;
  tradingDays?: number;
  swing?: number;
  balance?: string;
  balanceAt?: string;
  accountPayouts?: string;
  paid?: string;
  payoutRatio?: string;
  amount?: string;
  rithmicDeduction?: string;
  rithmicDeductionAt?: string;
  method?: string;
  note?: string;
  currentStatus?: string;
};

interface ProductPayoutConfiguration {
  product_id: number;
  min_payout: number;
  max_payout: number;
  payout_number: number;
  payout_request_after_days: number;
  split_start_amount: number;
  split_percentage: number;
  apply_to_copy_account: boolean;
  copy_account_amount: number;
  daily_profit_required: boolean;
  daily_profit_required_amount: number;
  required_balance_threshold_max: number;
  id: number;
}

interface PayoutStatus {
  payout_number: number;
  last_payout_date: string | null;
  trading_days_since_last_payout: number;
  flipping_days: number;
  flipping_days_percent: string;
  flipping_rule_passed: boolean;
  active_payout_in_process: boolean;
  thirty_percent_profit_rule: string;
  thirty_percent_profit_rule_rate: string;
  profit_highest: string;
  trading_days_required: number;
  earliest_next_allowed_payout_date: string;
  profit_current: string;
  profit_balance_target: number;
  min_payout_amount: number;
  max_payout_amount: number;
  split: number;
  status: string | null;
  trading_days_text: string | null;
  payout_days: string | null;
  date_eligible: boolean;
}

interface PerformanceAccountAddon {
  id: number;
  title: string;
  addon_type: string;
  addon_lifetime_performance_account_package_id: number | null;
  addon_trading_days_required: number;
  addon_discount_percent: number | null;
  addon_valid_for_days: number | null;
  addon_extra_amount_allowed_payout: number | null;
  addon_new_percent_override_default: number | null;
  addon_extra_allowed_payout_time: string | null;
  addon_disable_percent_default: number | null;
  addon_packages: number[];
  allow_reset_of_packages: boolean | null;
  addon_disable_at: string | null;
}

export interface PaAccounts extends TAwaitingApproval {
  payout_status: PayoutStatus;
  payout_logics: ProductPayoutConfiguration[];
  addons: PerformanceAccountAddon[];
}

export enum DialogStatus {
  Request,
  Approve,
}

export enum AccountChart {
  BartActive,
  BartClosed,
  BartLine,
  OnlyLine,
}

export interface StackedData {
  date: string;
  wining: number;
  losing: number;
  contracts?: number;
  micro?: number;
  mini?: number;
}
export interface BalanceStackedData {
  date: string;
  account_balance: number;
  net_pl: number;
  other?: number;
}

export const schemaApprovePayoutRequest = yup.object({
  approve_action: yup.string().required('Approve action is required!'),
  amount_is_correct: yup.boolean().optional(),
  payment_method: yup.string().required('Payment is required!'),
  payment_method_note: yup.string().optional(),
  make_rithmic_deduction: yup.boolean().optional(),
  cb_rithmic_credit: yup.boolean().optional(),
  deny_reason: yup.string().optional(),
});

export type ApprovePayoutRequestFormData = yup.InferType<
  typeof schemaApprovePayoutRequest
>;

export const schemaCompletePayoutRequest = yup.object({
  note: yup.string().optional(),
  confirm_rithmic: yup.boolean().optional(),
  ach_flag: yup.boolean().optional(),
});

export type CompletePayoutRequestFormData = yup.InferType<
  typeof schemaCompletePayoutRequest
>;

export type PayoutActionParams = {
  payout_id: number;
  current_user_id: number;
  approve_action: string;
  amount_is_correct?: boolean;
  pay_by_riseworks?: boolean;
  make_rithmic_deduction?: boolean;
  deny_reason?: string;
};
